import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import isEmail from 'validator/es/lib/isEmail';
import * as Yup from 'yup';
import { Button, EmailVerificationForm, FieldWrapper, FormError, Input, Modal, ModalContent, ModalFooter, ModalFormLayout, ModalHeader, ModalTitle } from 'components';
import Form from 'screens/Error/Form';
import { selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { selectCurrentUser, selectCurrentUserIdentities, selectVerifiedIdentities, updateIdentityVerificationCodeRequest, updateUserEmailRequest, User } from 'store/User';
import { IApiErrorPayloadWithCode, IdentityType, IUpdateEmailFormPayload, RequestStatus } from 'types';
import { getIdentityByType } from 'utils/identity';
import { MissingMethodMessage, SettingsEmailText, SettingsFieldWrapper, SettingsSectionContent } from './styles';

const FormEmail = () => {
  type UpdateScreen = 'EnterEmail' | 'EnterVerification';
  const [emailVerify, setEmailVerify] = useState<string>('');
  const [modalContent, setModalContent] = useState<UpdateScreen>('EnterEmail');
  const [modalActive, setModalActive] = useState<boolean>(false);
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_USER_EMAIL_REQUEST)) as IApiErrorPayloadWithCode;

  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST)) as IApiErrorPayloadWithCode;

  const { id: userId } = useSelector(selectCurrentUser());
  const userLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_USER_EMAIL_REQUEST));
  const verifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST));
  const status = useSelector((state: IStore) => selectAPIRequestStatus(state, User.UPDATE_USER_EMAIL_REQUEST));
  const verifyStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST));
  const currentUserIdentities = useSelector(selectVerifiedIdentities());
  const identities = useSelector(selectCurrentUserIdentities());

  const dispatch = useDispatch();
  const triggerChangeEmail = () => {
    setModalContent('EnterEmail');
    setModalActive(true);
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is a required field')
      .test('IsEmail', 'Invalid email address.', (email: string) => isEmail(email || '')),
    confirmEmail: Yup.string()
      .required('Confirmation email is a required field')
      .test('ConfirmEmail', 'Invalid email address', (email: string) => isEmail(email || ''))
      .test('emails-match', 'Emails must match', (value, ctx) => ctx.parent.email === value),
  });

  const updateIdentity = (identityValue: string, toastMessage?: string) => {
    dispatch(updateUserEmailRequest(identityValue, toastMessage));
  };

  const { errors, touched, isValid, dirty, getFieldProps, handleSubmit, resetForm, values } = useFormik({
    initialValues: {
      userId,
      email: '',
      confirmEmail: '',
    },
    onSubmit: (submittedValues: IUpdateEmailFormPayload) => {
      updateIdentity(submittedValues.email);
    },
    validateOnBlur: true,
    validationSchema,
  });

  useEffect(() => {
    if (status === RequestStatus.SUCCESS) {
      setEmailVerify(values.email);
      setModalContent('EnterVerification');
    }
  }, [status, values.email]);

  useEffect(() => {
    if (verifyStatus === RequestStatus.SUCCESS) {
      setModalActive(false);
      resetForm();
    }
  }, [verifyStatus, resetForm]);

  const validateField = (field: 'email' | 'confirmEmail') => Boolean(touched[field] && errors[field]);

  const onVerifySubmit = (verificationCode: string) => {
    const emailIdentity = (identities || []).find((id) => id.value === emailVerify);

    dispatch(updateIdentityVerificationCodeRequest(emailIdentity?.id, verificationCode, 'Email updated successfully'));
  };

  const resendSms = () => {
    updateIdentity(emailVerify, 'Verification code resent successfully');
  };
  const verificationForm = <EmailVerificationForm email={emailVerify} isResendLoading={userLoading} isLoading={verifyLoading} errorMessages={apiErrorsVerify?.errorMessages} onSubmit={onVerifySubmit} resendVerificationCode={resendSms} />;

  const currentEmailIdentity = getIdentityByType(currentUserIdentities, IdentityType.EMAIL)?.value;
  const noEmail = !currentEmailIdentity;

  const emailForm = (
    <>
      <ModalHeader>
        <ModalTitle>{noEmail ? 'Add ' : 'Change '} Email</ModalTitle>
      </ModalHeader>

      <ModalContent>
        <Form formId={User.UPDATE_USER_EMAIL_REQUEST} onSubmit={handleSubmit} dataCy="account-settings-contact-email-form">
          <ModalFormLayout>
            <FieldWrapper label="New Email" hasError={validateField('email')} message={validateField('email') ? errors.email : ''}>
              <Input type="email" disabled={userLoading} placeholder="you@email.com" {...getFieldProps('email')} />
            </FieldWrapper>

            <FieldWrapper label="Confirm Email" hasError={validateField('confirmEmail')} message={validateField('confirmEmail') ? errors.confirmEmail : ''}>
              <Input type="email" disabled={userLoading} placeholder="you@email.com" {...getFieldProps('confirmEmail')} />
            </FieldWrapper>
            {apiErrors?.errorMessages && apiErrors.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}
          </ModalFormLayout>
          <ModalFooter columns={1}>
            <Button type="submit" appearance="primary" size="large" label="Submit" disabled={userLoading || !(isValid && dirty)} isProcessing={userLoading} data-cy="email-modal-submit-button" />
          </ModalFooter>
        </Form>
      </ModalContent>
    </>
  );

  return (
    <>
      <Form formId={User.UPDATE_USER_EMAIL_REQUEST} dataCy="account-settings-contact-email-form">
        <SettingsSectionContent columns="auto">
          <SettingsFieldWrapper label="Email" data-cy="email-field">
            {noEmail && <MissingMethodMessage>Add an email to make your account more secure. You will be able to log in with this email too.</MissingMethodMessage>}
            {currentEmailIdentity && (
              <SettingsEmailText size="small" data-cy="email-display">
                {currentEmailIdentity}
              </SettingsEmailText>
            )}
          </SettingsFieldWrapper>

          <Button label={noEmail ? 'Add' : 'Edit'} type="button" appearance="primary" size="small" onClick={triggerChangeEmail} data-cy="change-email-button" />
        </SettingsSectionContent>
      </Form>

      <Modal
        maxWidth={475}
        isActive={modalActive}
        onClose={() => {
          setModalActive(!modalActive);
          resetForm();
        }}
      >
        <>
          {modalContent === 'EnterVerification' && verificationForm}
          {modalContent === 'EnterEmail' && emailForm}
        </>
      </Modal>
    </>
  );
};

export default FormEmail;
